import { Button as MantineButton, UnstyledButton as MantineUnstyledButton } from "@mantine/core";
import { polymorphicComponentWrapper } from "../../utilities";
import styles from "./button.module.scss";
import type {
    CustomButtonFactory,
    IProps,
    ISkeletonProps,
    IUnstyledButtonProps,
    UnstyledButtonFactory,
} from "./button.types";
import { Icon } from "../icon";
import { Skeleton } from "../skeleton";
import { buttonOverrides } from "../../theme";

/**
 * Button component with icon support and various variants.
 * @param {IconDefinition} props.icon - Optional icon to display on the left side of the button.
 * @param {string} props.variant - The visual style variant of the button.
 * @returns {JSX.Element} The Button component.
 */
const Button = polymorphicComponentWrapper<CustomButtonFactory, IProps>(
    "Button",
    ({ icon, variant = "filled", ...otherProps }, ref) => {
        return (
            <MantineButton
                ref={ref}
                variant={variant}
                classNames={{ root: styles["root"], section: styles["section"] }}
                leftSection={icon ? <Icon size="sm" icon={icon} /> : undefined}
                {...otherProps}
            />
        );
    }
);

/**
 * Unstyled button component without default styling.
 * @returns {JSX.Element} The UnstyledButton component.
 */
const UnstyledButton = polymorphicComponentWrapper<UnstyledButtonFactory, IUnstyledButtonProps>(
    "UnstyledButton",
    (props, ref) => <MantineUnstyledButton ref={ref} {...props} />
);

/**
 * Skeleton loader component for Button.
 * @param {string} props.size - The size of the button skeleton.
 * @returns {JSX.Element} The ButtonSkeleton component.
 */
const ButtonSkeleton = ({ size, ...props }: ISkeletonProps) => {
    return <Skeleton __vars={buttonOverrides} data-size={size} classNames={{ root: styles["skeleton"] }} {...props} />;
};

Button.Unstyled = UnstyledButton;
Button.Skeleton = ButtonSkeleton;
Button.Group = MantineButton.Group;
Button.Group.Section = MantineButton.Group.Section;

export { Button, ButtonSkeleton, UnstyledButton };
